import React, { useState, useEffect } from 'react'
import { Calendar } from 'lucide-react'
import { supabase, SecretEvent } from '../lib/supabase'
import { getEventStatus } from '../lib/utils/eventStatus'
import EventCard from '../components/EventCard'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'
import useAsset from '../../hooks/useAsset'

interface CapacityData {
  current_tail_count: number
  max_lobster_tails: number
  remaining_capacity: number
  percent_used: number
}

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<SecretEvent[]>([])
  const [capacityData, setCapacityData] = useState<Record<string, CapacityData>>({})
  const [loading, setLoading] = useState(true)
  const [isVideoLoading, setIsVideoLoading] = useState(true)
  const lobsterDrawing = useAsset('/lobster-drawing.png')

  useEffect(() => {
    loadEvents()
  }, [])

  const loadCapacityData = async (eventList: SecretEvent[]) => {
    try {
      const eventsWithCapacity = eventList.filter(e => e.max_lobster_tails && e.max_lobster_tails > 0)

      if (eventsWithCapacity.length === 0) {
        return
      }

      const capacityPromises = eventsWithCapacity.map(async (event) => {
        try {
          const response = await fetch(`/.netlify/functions/get-event-capacity?event_id=${event.id}`)
          if (!response.ok) {
            console.warn(`Failed to fetch capacity for event ${event.id}`)
            return null
          }
          const data = await response.json()
          return { eventId: event.id, capacity: data }
        } catch (err) {
          console.warn(`Error fetching capacity for event ${event.id}:`, err)
          return null
        }
      })

      const results = await Promise.all(capacityPromises)

      const capacityMap: Record<string, CapacityData> = {}
      results.forEach(result => {
        if (result && result.capacity) {
          capacityMap[result.eventId] = {
            current_tail_count: result.capacity.current_tail_count,
            max_lobster_tails: result.capacity.max_lobster_tails,
            remaining_capacity: result.capacity.remaining_capacity,
            percent_used: result.capacity.percent_used
          }
        }
      })

      setCapacityData(capacityMap)
      console.log('[EventsPage] Loaded capacity data for', Object.keys(capacityMap).length, 'events')
    } catch (error) {
      console.error('[EventsPage] Error loading capacity data:', error)
    }
  }

  const loadEvents = async () => {
    try {
      setLoading(true)
      console.log('[EventsPage] Loading events...')

      // Fetch all non-draft, non-cancelled events, sorted by creation date (newest first)
      const { data, error } = await supabase
        .from('secret_events')
        .select('*')
        .not('status', 'in', '("draft","cancelled")')
        .order('created_at', { ascending: false })

      console.log('[EventsPage] Raw data from DB:', data)
      if (error) {
        console.error('[EventsPage] Database error:', error)
        throw error
      }

      // Filter to show events that customers should see:
      // - Scheduled: enrollment opens soon (can see what's coming)
      // - Active: enrollment currently open (can place orders)
      // - Enrollment Closed: enrollment ended but event hasn't started (already ordered)
      // - In Progress: event is currently happening (need pickup info)
      // - Completed: past events (portfolio/history)
      const visibleEvents = (data || []).filter(event => {
        const status = getEventStatus(event)
        console.log('[EventsPage] Event:', event.title, 'Status:', status, 'Image:', event.image_url)
        return status === 'Scheduled' || status === 'Active' || status === 'Enrollment Closed' || status === 'In Progress' || status === 'Completed'
      })
      console.log('[EventsPage] Visible events after filter:', visibleEvents.length)
      setEvents(visibleEvents)

      // Fetch capacity data for events with lobster tail limits
      await loadCapacityData(visibleEvents)

    } catch (error: any) {
      console.error('Failed to load events:', error)
      toast.custom((t) => (
        <EnterpriseToast
          t={t}
          type="error"
          title="Loading Failed"
          message="Could not load events. Please try again."
        />
      ))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-secret-text-primary mb-2">
          SECRET Events
        </h1>
        <p className="text-secret-text-secondary">
          Exclusive popup events available only to SECRET members
        </p>
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="relative flex flex-col items-center justify-center py-16">
          <img
            src={lobsterDrawing}
            alt="Loading"
            className="w-[150px] h-[150px] animate-spin-slow"
            style={{ animationDirection: 'reverse' }}
          />
          <p className="mt-4 text-maroon-800 font-light text-lg">Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="secret-card text-center py-16">
          <Calendar className="w-20 h-20 text-secret-text-tertiary mx-auto mb-6" />
          <h2 className="text-xl font-bold text-secret-text-primary mb-4">
            No Events Available Right Now
          </h2>
          <p className="text-secret-text-secondary max-w-md mx-auto">
            You'll receive advance notification when new SECRET popup events are scheduled.
            Stay tuned for exclusive access!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              capacity={capacityData[event.id] || null}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default EventsPage
